import { IconButton, OnOffSwitch } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { CrmManagementDisabledTooltip } from "app/pages/sales-intelligence/common-components/CrmManagementDisabledTooltip";
import { WrappedWithPreloader } from "app/pages/sales-intelligence/pages/management/components/common/WrappedWithPreloader/WrappedWithPreloader";
import { MANAGEMENT_COMMON_UPGRADE } from "app/pages/sales-intelligence/pages/management/translations/managementKeys";

import {
    MANAGEMENT_EXPORT_PREFERENCES_REQUIRED_FIELDS_WARNING,
    MANAGEMENT_EXPORT_PREFERENCES_MAPPED_FIELDS,
} from "../../../../common-components/export-table/translations";
import { useExportToggleProps } from "../../hooks/useExportToggleProps";

import {
    StyledExportLabel,
    StyledExportSwitchContainer,
    StyledExportToggle,
    StyledLockedState,
    StyledLockIcon,
    StyledMandatoryFieldsWarning,
    StyledMappedFields,
} from "./style";
import type { ExportToggleProps } from "./type";

const ExportToggle = ({ integrationName, toggleName }: ExportToggleProps) => {
    const { value, isLoading, isLocked, text, mappedFieldsCount, totalFields, missingRequiredFieldsCount, onClick } =
        useExportToggleProps(integrationName, toggleName);

    const renderOnOffSwitch = (isReadOnly: boolean) => (
        <OnOffSwitch
            isSelected={value}
            onClick={onClick}
            isDisabled={isReadOnly}
        />
    );

    const renderSwitch = () => {
        return isLocked ? (
            <StyledLockedState>
                <StyledLockIcon
                    iconName="locked"
                    size="sm"
                />
                <StyledExportLabel>{translate(text)}</StyledExportLabel>
            </StyledLockedState>
        ) : (
            <StyledExportSwitchContainer>
                <CrmManagementDisabledTooltip renderContent={renderOnOffSwitch} />
                <StyledExportLabel>{translate(text)}</StyledExportLabel>
            </StyledExportSwitchContainer>
        );
    };

    const renderUpgrade = () => {
        if (isLocked) {
            return (
                <IconButton
                    iconName="sparkles"
                    type="trial"
                    onClick={onClick}
                >
                    {translate(MANAGEMENT_COMMON_UPGRADE)}
                </IconButton>
            );
        }

        const mappedFieldsCountText = `${mappedFieldsCount}/${totalFields} ${translate(
            MANAGEMENT_EXPORT_PREFERENCES_MAPPED_FIELDS,
        )} `;

        const shouldShowWarning = missingRequiredFieldsCount > 0 && value;

        const plurality = missingRequiredFieldsCount === 1 ? "singular" : "plural";

        const missingRequiredFieldsMessage = translate(
            MANAGEMENT_EXPORT_PREFERENCES_REQUIRED_FIELDS_WARNING[plurality],
            { count: missingRequiredFieldsCount },
        );

        return (
            <>
                {shouldShowWarning ? (
                    <StyledMandatoryFieldsWarning>{missingRequiredFieldsMessage}</StyledMandatoryFieldsWarning>
                ) : null}
                <StyledMappedFields>{mappedFieldsCountText}</StyledMappedFields>
            </>
        );
    };

    return (
        <StyledExportToggle data-automation={`${integrationName}-export-toggle-${toggleName}`}>
            <WrappedWithPreloader
                isLoading={isLoading}
                height={20}
                width="90%"
            >
                {renderSwitch()}
                {renderUpgrade()}
            </WrappedWithPreloader>
        </StyledExportToggle>
    );
};

export default ExportToggle;
